import { useState, useEffect } from "react";
import { ArrowUp, Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { money } from "@/lib/format";
import { createTopupCheckout } from "@/lib/api";
import { toast } from "sonner";

export const RaiseBidDialog = ({ open, onOpenChange, listing, topBid = 0 }) => {
    const current = listing ? Number(listing.bid_amount) : 0;
    const minReach = Math.max(topBid + 1, current + 1);
    const [desired, setDesired] = useState(minReach);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open && listing) setDesired(Math.max(topBid + 1, Number(listing.bid_amount) + 1));
    }, [open, listing, topBid]);

    if (!listing) return null;

    const desiredNum = Number(desired);
    const isWhole = Number.isInteger(desiredNum);
    const diff = desiredNum - current;
    const reachesTop = desiredNum >= topBid + 1;
    const valid = isWhole && diff > 0 && reachesTop;

    let helper = `You pay only the difference. Must reach at least ${money(minReach)} to move up.`;
    if (!isWhole) helper = "Enter a whole dollar amount.";
    else if (diff <= 0) helper = "New total must be higher than your current bid.";
    else if (!reachesTop) helper = `To move up you must reach at least ${money(topBid + 1)} (current #1 is ${money(topBid)}).`;

    const submit = async () => {
        if (!valid) return;
        setBusy(true);
        try {
            const res = await createTopupCheckout(listing.id, {
                desired_total: desiredNum,
                email: email || undefined,
            });
            window.location.assign(res.checkout_url);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not start checkout. Try again.";
            toast.error(msg);
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-surface-1 border-border" data-testid="raise-bid-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading flex items-center gap-2">
                        <ArrowUp className="h-5 w-5 text-heat" /> Raise bid — {listing.display_name}
                    </DialogTitle>
                    <DialogDescription>
                        Current bid <span className="font-semibold text-foreground tabular">{money(current)}</span>. Pay only the difference to climb.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="raise-total">New total bid (USD)</Label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input
                                id="raise-total"
                                data-testid="raise-bid-input"
                                type="number"
                                min={minReach}
                                step="1"
                                value={desired}
                                onChange={(e) => setDesired(e.target.value)}
                                className="pl-7 tabular"
                            />
                        </div>
                        <p className={`text-xs ${valid ? "text-muted-foreground" : "text-heat"}`}>{helper}</p>
                    </div>

                    <div className="flex items-center justify-between rounded-lg bg-secondary/60 px-3 py-2.5">
                        <span className="text-sm text-muted-foreground">You pay now</span>
                        <span className="font-heading text-xl font-bold tabular text-heat">
                            {money(diff > 0 ? diff : 0)}
                        </span>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="raise-email">Email <span className="text-muted-foreground font-normal">(optional receipt)</span></Label>
                        <Input
                            id="raise-email"
                            data-testid="raise-bid-email-input"
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                </div>

                <Button
                    data-testid="raise-bid-submit-button"
                    disabled={!valid || busy}
                    onClick={submit}
                    className="w-full bg-heat text-white hover:bg-heat-dim font-semibold"
                >
                    {busy ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Opening checkout…</>
                    ) : (
                        <>Pay {money(diff > 0 ? diff : 0)} to raise bid</>
                    )}
                </Button>
            </DialogContent>
        </Dialog>
    );
};
